import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog } from '@material-ui/core';
import { adminClientsAPI, adminMemberUpdateAPI, adminMemberMileageAPI, adminMemberMileageAdjustAPI } from '../../api/admin';
import { Field, Input, Select, Textarea, Btn, Row, Grid, Badge, Msg, fmtDate } from './ui';
import tokens from '../theme/tokens';

// 기업회원 정보 모달 — 사업자번호 · 사업자등록증(첨부 보기) · 정산일 · 거래처 연결(운임·오더메모) · 할인률 · 마일리지 적립률/조정 · 법인카드 · 메모
const useStyles = makeStyles(() => ({
  paper: { width: 640, maxWidth: 'calc(100% - 32px)', padding: '28px 28px 24px', borderRadius: 20, boxShadow: tokens.dialogShadow },
  title: { margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: -0.9, color: tokens.ink },
  sub: { margin: '4px 0 16px', fontSize: 13, color: tokens.muted },
  sec: { marginTop: 18, paddingTop: 14, borderTop: `1px solid ${tokens.lineSoft}`, '& h4': { margin: '0 0 10px', fontSize: 14, color: tokens.ink } },
  logs: { maxHeight: 160, overflowY: 'auto', fontSize: 12, '& div': { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${tokens.lineSoft}` } },
  cert: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, borderRadius: 8, overflow: 'hidden',
    border: `1px solid ${tokens.line}`, backgroundColor: tokens.surfaceSoft,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  },
  certPdf: { fontSize: 12, fontWeight: 700, color: tokens.primaryDark },
  certMeta: { display: 'flex', flexDirection: 'column', fontSize: 13, color: tokens.ink, '& span': { marginTop: 2, fontSize: 12, color: tokens.muted } },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
}));

const SETTLE_DAYS = [0, ...Array.from({ length: 31 }, (_, i) => i + 1)];
const TYPE = { earn: '적립', use: '사용', adjust: '조정' };

function AdminMemberBizDialog({ member, onClose, onSaved }) {
  const cs = useStyles();
  const biz = (member && member.biz) || {};
  const [form, setForm] = useState({
    bizNo: biz.bizNo || '', settleDay: biz.settleDay || 0, clientKey: biz.clientKey || '', clientName: biz.clientName || '',
    discountRate: biz.discountRate || 0, mileageRate: biz.mileageRate == null ? '' : biz.mileageRate, note: (member && member.biz && member.biz.note) || '',
  });
  const [clients, setClients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [adj, setAdj] = useState({ amount: '', memo: '' });
  const [msg, setMsg] = useState({ ok: false, text: '' });
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(member);

  useEffect(() => {
    adminClientsAPI().then((resp) => { if (resp && resp.status === 200) setClients(resp.data.rows || []); });
    adminMemberMileageAPI(member.id).then((resp) => { if (resp && resp.status === 200) setLogs(resp.data.logs || []); });
  }, [member.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickClient = (e) => {
    const key = e.target.value;
    const c = clients.find((x) => x.clientKey === key);
    setForm((f) => ({ ...f, clientKey: key, clientName: c ? (c.bizName || c.clientName) : f.clientName }));
  };

  const save = async (extra = {}) => {
    setBusy(true); setMsg({ ok: false, text: '' });
    const resp = await adminMemberUpdateAPI(member.id, { biz: { ...form, mileageRate: form.mileageRate === '' ? null : form.mileageRate, ...extra } });
    setBusy(false);
    if (resp && resp.status === 200) { setCurrent(resp.data.member); onSaved(resp.data.member); setMsg({ ok: true, text: '저장했습니다.' }); }
    else setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '저장하지 못했습니다.' });
  };
  const adjust = async () => {
    const amount = Number(String(adj.amount).replace(/[^0-9-]/g, ''));
    if (!amount) return;
    const resp = await adminMemberMileageAdjustAPI(member.id, { amount, memo: adj.memo });
    if (resp && resp.status === 200) { setCurrent(resp.data.member); onSaved(resp.data.member); setLogs(resp.data.logs || []); setAdj({ amount: '', memo: '' }); setMsg({ ok: true, text: '마일리지를 조정했습니다.' }); }
    else setMsg({ ok: false, text: (resp && resp.data && resp.data.message) || '조정하지 못했습니다.' });
  };

  const card = current.biz && current.biz.card;
  const cert = current.biz && current.biz.certUrl ? current.biz : null;   // 가입 신청 때 첨부한 사업자등록증
  return (
    <Dialog open onClose={onClose} classes={{ paper: cs.paper }}>
      <h3 className={cs.title}>{current.companyName || current.name} · 기업회원 정보</h3>
      <p className={cs.sub}>{current.email} · {current.phone || '-'} · 가입 {fmtDate(current.createdAt)}</p>
      <Grid cols={3}>
        <Field label="사업자등록번호"><Input value={form.bizNo} onChange={set('bizNo')} placeholder="숫자 10자리" inputMode="numeric" /></Field>
        <Field label="정산일">
          <Select value={form.settleDay} onChange={set('settleDay')}>
            {SETTLE_DAYS.map((d) => <option key={d} value={d}>{d === 0 ? '말일' : `${d}일`}</option>)}
          </Select>
        </Field>
        <Field label="거래처 할인률 (%)"><Input type="number" min="0" max="100" value={form.discountRate} onChange={set('discountRate')} /></Field>
      </Grid>
      <Grid cols={2}>
        <Field label="거래처 연결 (운임표·오더메모)">
          <Select value={form.clientKey} onChange={pickClient}>
            <option value="">연결 안 함 (일반운임)</option>
            {clients.map((c) => <option key={c.clientKey} value={c.clientKey}>{c.clientName}{c.bizName && c.bizName !== c.clientName ? ` · ${c.bizName}` : ''}</option>)}
          </Select>
        </Field>
        <Field label="오더메모 거래처명"><Input value={form.clientName} onChange={set('clientName')} placeholder="비우면 상호" /></Field>
      </Grid>
      <Grid cols={2}>
        <Field label="마일리지 적립률 (%) · 비우면 기본"><Input type="number" min="0" max="100" value={form.mileageRate} onChange={set('mileageRate')} /></Field>
        <Field label="법인카드">
          <Row>
            <Badge tone={card ? 'on' : 'off'}>{card ? `${card.company} **** ${card.last4} (${card.expMonth}/${card.expYear})` : '미등록'}</Badge>
            {card && <Btn tone="danger" size="sm" onClick={() => save({ clearCard: true })}>삭제</Btn>}
          </Row>
        </Field>
      </Grid>
      <Field label="사업자등록증">
        {cert ? (
          <Row>
            <a className={cs.cert} href={cert.certUrl} target="_blank" rel="noopener noreferrer" aria-label="사업자등록증 크게 보기">
              {/\.pdf$/i.test(cert.certUrl) ? <span className={cs.certPdf}>PDF</span> : <img src={cert.certUrl} alt="" />}
            </a>
            <span className={cs.certMeta}>{cert.certName || '첨부 파일'}<span>{fmtDate(cert.certAt)}</span></span>
            <Btn tone="line" size="sm" onClick={() => window.open(cert.certUrl, '_blank', 'noopener')}>크게 보기</Btn>
          </Row>
        ) : <Badge tone="off">미첨부</Badge>}
      </Field>
      <Field label="메모"><Textarea value={form.note} onChange={set('note')} style={{ minHeight: 64 }} /></Field>
      <Row><Btn onClick={() => save()} disabled={busy}>{busy ? '저장 중' : '저장'}</Btn><Msg ok={msg.ok}>{msg.text}</Msg></Row>

      <div className={cs.sec}>
        <h4>마일리지 · 잔액 {Number(current.mileage || 0).toLocaleString('ko-KR')}P</h4>
        <Row>
          <Input value={adj.amount} onChange={(e) => setAdj((a) => ({ ...a, amount: e.target.value }))} placeholder="+적립 / −차감" style={{ width: 140, height: 36 }} />
          <Input value={adj.memo} onChange={(e) => setAdj((a) => ({ ...a, memo: e.target.value }))} placeholder="메모" style={{ width: 220, height: 36 }} />
          <Btn tone="line" size="sm" onClick={adjust} disabled={!adj.amount}>조정</Btn>
        </Row>
        {logs.length > 0 && (
          <div className={cs.logs} style={{ marginTop: 8 }}>
            {logs.map((l) => <div key={l.id}><span>{fmtDate(l.at)} · {TYPE[l.type] || l.type}{l.memo ? ` · ${l.memo}` : ''}{l.orderNumber ? ` · ${l.orderNumber}` : ''}</span><span>{l.amount > 0 ? '+' : ''}{l.amount.toLocaleString('ko-KR')} → {l.balanceAfter.toLocaleString('ko-KR')}</span></div>)}
          </div>
        )}
      </div>
      <div className={cs.foot}><Btn tone="ghost" onClick={onClose}>닫기</Btn></div>
    </Dialog>
  );
}

export default AdminMemberBizDialog;
